/** Whether the visitor prefers reduced motion for scroll animations. */
const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Scrolls the viewport to an in-page section identified by a hash fragment (e.g. `#food-how`).
 * Returns true when the target element exists in the DOM.
 */
export const scrollToHashSection = (hash: string): boolean => {
  if (typeof document === "undefined" || !hash) return false;

  const target = document.getElementById(hash.replace("#", ""));
  if (!target) return false;

  target.scrollIntoView({
    behavior: prefersReducedMotion() ? "auto" : "smooth",
    block: "start",
  });

  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
  }
  target.focus({ preventScroll: true });

  return true;
};

/**
 * Schedules hash scrolling after layout/fonts settle, retrying briefly when the target
 * mounts asynchronously after a route change.
 */
export const scheduleScrollToHashSection = (
  hash: string,
  onAttempt?: (found: boolean) => void
): (() => void) => {
  let cancelled = false;
  let frame = 0;
  let retryTimer: number | undefined;

  const attemptScroll = () => {
    if (cancelled) return;

    frame = window.requestAnimationFrame(() => {
      const found = scrollToHashSection(hash);
      onAttempt?.(found);

      if (!found && !cancelled) {
        retryTimer = window.setTimeout(() => {
          const retryFound = scrollToHashSection(hash);
          onAttempt?.(retryFound);
        }, 150);
      }
    });
  };

  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (fonts?.ready) {
    fonts.ready.then(attemptScroll).catch(attemptScroll);
  } else {
    attemptScroll();
  }

  return () => {
    cancelled = true;
    window.cancelAnimationFrame(frame);
    if (retryTimer !== undefined) {
      window.clearTimeout(retryTimer);
    }
  };
};
